import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';

import { SecondaryButton } from '@components/Button/variants';
import IconArrow from '@components/Images/IconArrow';
import Spinner from '@components/Spinner';
import useEventTracking from '@hooks/useEventTracking';
import useEvents from '@hooks/useEvents';
import { event_path } from '@routes';
import { ClickTarget, click } from '@tracking';
import { Event, EventsResult, SanitizedEventParams, getEvents } from '@utils/backend-api/events';

const MORE_PAST_EVENTS_TEXT = 'More past events';

const PastEvents = () => {
  const [pastEvents, setPastEventList] = useState<Event[]>([]);
  const { events, isFetchingMoreEvents } = useEvents();
  const [nextPage, setNextPage] = useState<number | null>(null);
  const { trackEvent } = useEventTracking();

  useEffect(() => {
    if (!isFetchingMoreEvents) {
      setPastEventList(events.pastEvents);
      setNextPage(events.nextPage);
    }
  }, [isFetchingMoreEvents, events?.nextPage, events?.pastEvents]);

  const { mutate: fetchmoreEvents, isLoading: isFetchMoreEvents } = useMutation(
    async (parameters: SanitizedEventParams) => await getEvents(parameters),
    {
      onSuccess: (data: EventsResult) => {
        setPastEventList((prevState) => [...prevState, ...data.pastEvents]);
        setNextPage(data.nextPage);
      },
    }
  );

  return (
    <div className="mb-112 px-12">
      <div className="m-auto max-w-container-lg">
        <h3 className="border-0 border-b border-solid border-grayscale-beige pb-32 font-suisse-screen text-headline-lg">
          Past Events
        </h3>
        <div className="flex flex-col">
          {isFetchingMoreEvents ? (
            <div className="flex justify-center">
              <Spinner />
            </div>
          ) : (
            pastEvents.map((event) => (
              <div
                key={event.slug}
                className="flex cursor-pointer items-center justify-between border-0 border-b border-solid border-grayscale-gray py-32 first:pt-0"
                onClick={() => (window.location.href = event_path(event.slug))}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.PastEvent, clickText: event.name }))
                }
              >
                <div className="flex flex-col">
                  <div className="font-suisse-works text-headline-sm">{event.name}</div>
                  <div className="font-suisse-neue text-headline-alt-sm text-grayscale-gray">
                    {event.date} {event.location && `| ${event.location}`}
                  </div>
                </div>
                <SecondaryButton
                  as="link"
                  href={event_path(event.slug)}
                  className="group relative !h-[34px] !w-[34px] rounded-56 !border !border-solid border-primary-magenta !p-0 hover:bg-primary-magenta"
                  onClickCapture={() =>
                    trackEvent(click({ clickTarget: ClickTarget.PastEvent, clickText: event.name }))
                  }
                >
                  <IconArrow className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rotate-180 text-primary-magenta" />
                </SecondaryButton>
              </div>
            ))
          )}
          {nextPage && (
            <div className="mt-32 flex justify-center">
              <SecondaryButton
                as="button"
                onClick={() => fetchmoreEvents({ page: nextPage })}
                className="w-full"
                onClickCapture={() =>
                  trackEvent(
                    click({
                      clickTarget: ClickTarget.PastEventMore,
                      clickText: MORE_PAST_EVENTS_TEXT,
                    })
                  )
                }
              >
                {isFetchMoreEvents ? <Spinner /> : MORE_PAST_EVENTS_TEXT}
              </SecondaryButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PastEvents;
